import * as React from 'react';
import Modal from 'antd/lib/modal/Modal';
import { ModalProps } from 'antd/lib/modal';
import Form from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Select from 'antd/lib/select';
import Text from 'antd/lib/typography/Text';
import { TriageTicketFilters } from '@/types';

export type TriageFilterField = keyof TriageTicketFilters;

export const TRIAGE_FILTER_FIELDS: { value: TriageFilterField; label: string; description: string }[] = [
    {
        value: 'issueTypes',
        label: 'Issue type',
        description: 'Tickets of these types count as a bug, replacing the default Bug type.',
    },
    {
        value: 'labels',
        label: 'Label',
        description: 'Tickets carrying one of these labels count as a bug.',
    },
    {
        value: 'titleKeywords',
        label: 'Title keyword',
        description: 'Only pull tickets whose title contains one of these, e.g. [iOS].',
    },
];

const DEFAULT_FIELD: TriageFilterField = TRIAGE_FILTER_FIELDS[0].value;

interface NewTicketFilterModalProps {
    open: boolean;
    isLoading: boolean;
    filters: TriageTicketFilters;
    onCancel: ModalProps['onCancel'];
    onOk: (field: TriageFilterField, values: string[]) => void;
}

const NewTicketFilterModal: React.FC<NewTicketFilterModalProps> = ({ open, isLoading, filters, onCancel, onOk }) => {
    const [field, setField] = React.useState<TriageFilterField>(DEFAULT_FIELD);
    const [values, setValues] = React.useState<string[]>(filters[DEFAULT_FIELD] ?? []);

    const selectField = (next: TriageFilterField) => {
        setField(next);
        setValues(filters[next] ?? []);
    };

    const reset = () => selectField(DEFAULT_FIELD);

    const description = TRIAGE_FILTER_FIELDS.find((option) => option.value === field)?.description;

    return (
        <Modal
            title="Edit ticket filters"
            open={open}
            okText="Save"
            okButtonProps={{ loading: isLoading }}
            onOk={() => onOk(field, values)}
            onCancel={onCancel}
            afterClose={reset}
        >
            <Form layout="vertical">
                <FormItem label="Filter">
                    <Select value={field} options={TRIAGE_FILTER_FIELDS} onChange={selectField} />
                </FormItem>
                <FormItem label="Values" extra={description}>
                    <Select
                        mode="tags"
                        allowClear
                        tokenSeparators={[',']}
                        dropdownStyle={{ display: 'none' }}
                        placeholder="Type a value and press enter"
                        value={values}
                        onChange={setValues}
                    />
                </FormItem>
                <Text type="secondary">
                    Issue types and labels both say what counts as a bug, so either one matching is enough. Title
                    keywords narrow that down further.
                </Text>
            </Form>
        </Modal>
    );
};

export default NewTicketFilterModal;
